import React from 'react';
import { Link } from "react-router-dom";
import { Shield, BookOpen, Users, MessageSquare, Scale } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

const ApologeticsOverviewSection = () => {
  const tools = [{
    title: "Theological Answers",
    description: "Get comprehensive biblical answers to challenging questions about faith.",
    icon: <BookOpen className="h-5 w-5" />,
    path: "/apologetics#biblical-defense"
  }, {
    title: "Addressing Atheism",
    description: "Engage in bold defense of Christian truth against secular challenges.",
    icon: <Shield className="h-5 w-5" />,
    path: "/apologetics#atheism"
  }, {
    title: "Interfaith Dialogue",
    description: "Engage in meaningful dialogue with people of other faiths while standing firm in biblical truth.",
    icon: <Users className="h-5 w-5" />,
    path: "/apologetics#interfaith-dialogue"
  }, {
    title: "Moral & Ethical Dilemmas",
    description: "Navigate complex moral and social issues with biblical wisdom.",
    icon: <Scale className="h-5 w-5" />,
    path: "/apologetics#moral-dilemmas"
  }, {
    title: "Evangelism Assistant",
    description: "Practice sharing your faith and engaging in respectful debate.",
    icon: <MessageSquare className="h-5 w-5" />,
    path: "/apologetics#evangelism-assistant"
  }];
  return <div className="w-full bg-white">
    <div className="bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-2 space-x-0">
          {/* Text content */}
          <div className="flex-1 text-center md:text-left md:pr-0 md:pl-16 px-4">
            <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} className="text-3xl md:text-5xl lg:text-6xl font-playfair text-[#4285f4] mb-2">
              APOLOGETICS
            </motion.h2>
            
            <motion.h3 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-xl md:text-2xl lg:text-3xl font-playfair text-[#1A1F2C] mb-4">
              Defend Your Faith With Confidence
            </motion.h3>
            
            <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="text-gray-600 text-xs md:text-sm lg:text-base max-w-[90%] mx-auto md:max-w-none md:mx-0">
              Our apologetics tools equip you with biblically sound answers to the 
              most challenging questions about Christianity, science, morality, 
              and other worldviews.
            </motion.p>
          </div>
        
          {/* Cross image */}
          <motion.img src="https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//fixed%20cross%20mask.png" alt="Apologetics" className="flex-1 max-h-[250px] md:max-h-[400px] w-auto object-contain mt-4 md:mt-0 md:ml-0" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} />
        </div>
      </div>
    </div>

    {/* Tools section */}
    <div className="bg-white py-4 md:py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="space-y-2 md:space-y-3">
          {tools.map((tool, index) => <motion.div key={tool.title} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }}>
              <Link to={tool.path}>
                <div className="group bg-[#F8F7F4] hover:bg-[#F0EDE6] transition-all duration-300 rounded-full p-2 md:p-4 px-3 md:px-6 flex items-center justify-between cursor-pointer">
                  <div>
                    <h4 className="font-medium text-sm md:text-base lg:text-lg text-[#1A1F2C]">{tool.title}</h4>
                    <p className="text-[10px] md:text-xs lg:text-sm text-gray-600">{tool.description}</p>
                  </div>
                  <div className="bg-white p-2 rounded-full shadow-sm group-hover:shadow-md transition-shadow">
                    {tool.icon}
                  </div>
                </div>
              </Link>
            </motion.div>)}
        </div>
      </div>
    </div>

    {/* New Try Apologetics Tools Button */}
    <div className="text-center mt-12">
      <Link to="/apologetics">
        <Button variant="blue" size="xl" className="mx-auto hover:bg-[#3367d6] transition-colors duration-300 text-white py-0 my-0">
          Try Apologetics Tools
        </Button>
      </Link>
    </div>
  </div>;
};

export default ApologeticsOverviewSection;
